import { useState, useEffect, useCallback } from "react";
import { EmailRecord, EmailStats } from "@/types/email";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useEmailStorage } from "./emails/useEmailStorage";
import { calculateEmailStats } from "./emails/useEmailStats";

export const useEmailManagement = () => {
  const [emails, setEmails] = useState<EmailRecord[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailStats, setEmailStats] = useState<EmailStats>({
    totalSent: 0,
    totalResponses: 0,
    positiveResponses: 0,
    responseRate: 0,
    total: 0,
    sent: 0,
    draft: 0,
  });
  const [dataFetched, setDataFetched] = useState(false);
  
  const { loadEmailData: loadEmailsFromStorage, saveEmailRecord, updateEmailRecord, deleteEmailRecord } = useEmailStorage();

  // Load emails from storage only once
  const loadEmailData = useCallback(async () => {
    if (dataFetched) return emails;
    
    setIsLoading(true);
    try {
      const loadedEmails = await loadEmailsFromStorage();
      setEmails(loadedEmails);
      
      // Update stats
      const stats = calculateEmailStats(loadedEmails);
      setEmailStats(stats);
      setDataFetched(true);
      
      return loadedEmails;
    } catch (error) {
      console.error("Error loading email data:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [loadEmailsFromStorage, dataFetched, emails]);

  // Save email and sync with storage
  const handleEmailSent = async (emailRecord: EmailRecord) => {
    try {
      setIsLoading(true);
      const updatedEmails = await saveEmailRecord(emailRecord);
      setEmails(updatedEmails);
      
      // Update stats
      const stats = calculateEmailStats(updatedEmails);
      setEmailStats(stats);
      
      toast.success(
        emailRecord.status === "sent" 
          ? "Email sent successfully" 
          : "Email saved as draft"
      );
      
      return updatedEmails;
    } catch (error) {
      console.error("Error saving email:", error);
      toast.error("Failed to save email");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle viewing an email
  const handleViewEmail = (email: EmailRecord) => {
    setSelectedEmail(email);
  };

  // Handle closing email details view
  const handleCloseEmailDetails = () => {
    setSelectedEmail(null);
  };

  // Handle updating email details
  const handleEmailUpdated = async (updatedEmail: EmailRecord) => {
    try {
      setIsLoading(true);
      const updatedEmails = await updateEmailRecord(updatedEmail);
      setEmails(updatedEmails);
      setSelectedEmail(updatedEmail);
      
      // Update stats if needed (for example, when response status changes)
      const stats = calculateEmailStats(updatedEmails);
      setEmailStats(stats);
      
      return updatedEmails;
    } catch (error) {
      console.error("Error updating email:", error);
      toast.error("Failed to update email");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle deleting an email
  const handleDeleteEmail = async (emailToDelete: EmailRecord) => {
    try {
      setIsLoading(true);
      const updatedEmails = await deleteEmailRecord(emailToDelete.id);
      setEmails(updatedEmails);
      
      // If we're currently viewing this email, close the detail view
      if (selectedEmail && selectedEmail.id === emailToDelete.id) {
        setSelectedEmail(null);
      }
      
      // Update stats
      const stats = calculateEmailStats(updatedEmails);
      setEmailStats(stats);
      
      toast.success("Email deleted successfully");
      
      return updatedEmails;
    } catch (error) {
      console.error("Error deleting email:", error);
      toast.error("Failed to delete email");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle provider updates - now with optimizations
  const handleProviderUpdated = useCallback(() => {
    // Only reload data if we haven't fetched it yet
    if (!dataFetched) {
      loadEmailData();
    }
  }, [dataFetched, loadEmailData]);

  return {
    emails,
    selectedEmail,
    emailStats,
    isLoading,
    setIsLoading,
    loadEmailData,
    handleEmailSent,
    handleViewEmail,
    handleCloseEmailDetails,
    handleEmailUpdated,
    handleDeleteEmail,
    handleProviderUpdated
  };
};
